// What picture stands for an entry.
//
// The journal's gallery needs a picture per entry, and most entries have no picture of their own.
// Rather than let each surface improvise, this module answers the question once and returns a
// descriptor saying *what kind* of picture it chose, so a caller can tell an image URL apart from a
// CSS background and label it honestly.
//
// The rules, in order:
//   1. A photo the user actually has on the entry wins — their own picture beats anything generated.
//   2. A dream with a chosen cover uses that catalogue piece, so a pick of "phone" stays the phone
//      even if the first depicting symbol is a forest.
//   3. A dream gets the orb of one of its own symbols, preferring one we have real art for, so a
//      dream tagged "Mountains, Properties, Stand, Arm" shows the mountain and not an abstract wash.
//   4. A dream with nothing to draw on gets a stable aura — still dream language, just wordless.
//   5. A waking entry gets a warm dawn gradient. This is a placeholder (see WAKING_STOPS) standing
//      in for cover art that does not exist yet.
//
// Everything is derived from the entry id or its symbols, never from render order or Math.random, so
// the same entry always shows the same picture — a grid that reshuffles on every scroll reads as a bug.

import { artCatalogue, depictingStem, symbolArt } from '../symbols/art';
import type { EntryKind } from '../api/entries';
import type { PhotoCrop } from '../api/entry-photos';

/** The parts of an `Entry` a preview is derived from. A full `Entry` satisfies this. */
export interface PreviewEntry {
  id: string;
  kind: EntryKind;
  symbols: string[];
  /**
   * Catalogue stem the dreamer chose as the stand-in cover. A full `Entry` carries this on
   * `dreamDetails.coverArt` instead; both are read.
   */
  coverArt?: string | null;
  dreamDetails?: { coverArt?: string } | null;
}

export type EntryPreviewSource =
  /** A decrypted photo belonging to the entry, behind an object URL the caller owns. */
  | 'photo'
  /** Artwork that actually depicts one of the entry's symbols. */
  | 'symbol'
  /** A hashed abstract orb — a picture, but not one that means anything in particular. */
  | 'aura'
  /** The waking-entry stand-in, waiting on real cover art. */
  | 'placeholder';

export type EntryPreview =
  | {
      kind: 'image';
      source: Exclude<EntryPreviewSource, 'placeholder'>;
      /** Either a bundled asset URL or an object URL; both go straight into an `<img src>`. */
      url: string;
      /** What to call this picture. Empty means decorative — render `alt=""`. */
      label: string;
      /**
       * How the writer chose to sit their own photo in the frame. Only ever set on a `photo`
       * preview — generated art has no part of it the writer picked out.
       */
      crop?: PhotoCrop | null;
    }
  | {
      kind: 'gradient';
      source: 'placeholder';
      /** A complete CSS `background` value. */
      background: string;
      label: string;
    };

/**
 * Warm dawn stops for waking entries.
 *
 * PLACEHOLDER — this is the seam. When commissioned cover art for waking entries exists, replace the
 * `gradientPreview` branch below with a lookup that returns `{ kind: 'image', source: 'cover', … }`;
 * nothing outside this file needs to change, because callers already handle both descriptor shapes.
 *
 * The colours are written out rather than read from `--accent-warm-*` for two reasons. A gradient
 * built from custom properties can only ever be the one gradient those properties hold, and this
 * wants variety per entry; and this block stands in for a photograph, so like a photograph it should
 * look the same under dusk and dawn instead of inverting with the theme. They sit deliberately in the
 * same lightness/chroma band as `--accent-warm-1` (oklch 0.84 0.09 68) and `--accent-warm-2`
 * (oklch 0.80 0.10 38) in tokens.css, so a tile reads as Oreia's warm story in either theme. No text
 * is ever drawn on top of one, which is what keeps that safe.
 */
const WAKING_STOPS: readonly (readonly [string, string])[] = [
  ['oklch(0.88 0.070 78)', 'oklch(0.80 0.100 42)'], // gold → coral
  ['oklch(0.86 0.075 62)', 'oklch(0.78 0.105 28)'], // peach → rose
  ['oklch(0.89 0.060 88)', 'oklch(0.81 0.095 52)'], // straw → amber
  ['oklch(0.87 0.065 34)', 'oklch(0.79 0.100 66)'], // blush → honey
  ['oklch(0.90 0.055 70)', 'oklch(0.82 0.090 20)'], // cream → clay
];

const WAKING_ANGLES = [104, 120, 136, 152] as const;

/** FNV-1a, the same hash `symbols/art.ts` uses to pick an aura. Stable across builds and platforms. */
function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function gradientPreview(seed: string): EntryPreview {
  const [from, to] = WAKING_STOPS[hash(seed) % WAKING_STOPS.length];
  // A second, differently-salted hash: reusing the first would tie the angle to the palette and
  // collapse twenty combinations down to five.
  const angle = WAKING_ANGLES[hash(`${seed}/angle`) % WAKING_ANGLES.length];
  return {
    kind: 'gradient',
    source: 'placeholder',
    background: `linear-gradient(${angle}deg, ${from}, ${to})`,
    // Decorative: it depicts nothing, and the tile around it already carries the title and date.
    label: '',
  };
}

/**
 * The aura for a dream with no symbols to draw on.
 *
 * Picked out of `artCatalogue.auras` directly rather than by handing the entry id to `symbolArt`,
 * because that function is built to find meaning in a string and a UUID gives it plenty of chances
 * to succeed by accident — a hex group spelling "face" would resolve, via the alias table, to the
 * mask. This way a symbol-less dream can only ever get an aura.
 */
function auraPreview(seed: string): EntryPreview | null {
  const { auras } = artCatalogue;
  if (auras.length === 0) return null;
  return { kind: 'image', source: 'aura', url: auras[hash(seed) % auras.length], label: '' };
}

function chosenCover(entry: PreviewEntry): string | undefined {
  const stem = (entry.coverArt ?? entry.dreamDetails?.coverArt)?.trim();
  return stem || undefined;
}

function dreamPreview(entry: PreviewEntry): EntryPreview {
  const chosen = chosenCover(entry);
  if (chosen) {
    const direct = depictingStem(chosen);
    const art = direct ? undefined : symbolArt(chosen);
    const url = direct ?? (art?.depicts ? art.url : undefined);
    if (url) return { kind: 'image', source: 'symbol', url, label: chosen };
  }

  const named = entry.symbols.map((s) => s.trim()).filter(Boolean);

  // First pass over the symbols in the order the extraction wrote them, which is roughly order of
  // prominence, taking the first that has artwork actually depicting it. Only if none does do we
  // settle for an aura — and then it is the first symbol's aura, so the picture still belongs to
  // this dream rather than to its id.
  for (const name of named) {
    const art = symbolArt(name);
    if (art?.depicts) return { kind: 'image', source: 'symbol', url: art.url, label: name };
  }

  for (const name of named) {
    const art = symbolArt(name);
    if (art) return { kind: 'image', source: 'aura', url: art.url, label: '' };
  }

  // No symbols at all. An aura keeps a dream looking like a dream: the warm gradient is the waking
  // language, and borrowing it here would make the two kinds indistinguishable at a glance, which is
  // the one thing the gallery is meant to show.
  return auraPreview(entry.id) ?? gradientPreview(entry.id);
}

/**
 * The picture for an entry.
 *
 * `photoUrl` is an already-decrypted object URL for one of the entry's own photos, if the caller has
 * gone to the trouble of loading one. It always wins. The caller keeps ownership of that URL and is
 * the one that has to revoke it; this function only reads it.
 */
export function entryPreview(
  entry: PreviewEntry,
  photoUrl?: string | null,
  crop?: PhotoCrop | null,
): EntryPreview {
  if (photoUrl) {
    return { kind: 'image', source: 'photo', url: photoUrl, label: 'Photo from this entry', crop };
  }
  if (entry.kind === 'dream') return dreamPreview(entry);
  return gradientPreview(entry.id);
}
